import { Edit, RemoveCircle } from '@mui/icons-material';
import { Card, CardProps, IconButton, styled, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { AppRoutes } from '~/app/app.routes';
import { PromotionService } from '~/service/service.promotion';
import { PromotionBestsellerDto, PromotionNewbieDto, PromotionOfferDto } from '~/shared/dto/admin/promotion.dto';
import { useService } from '~/shared/service/service.base';
import { Box, Col, Img, Row, Text } from '~/view/view.box';

export type PromotionCommonDto = PromotionBestsellerDto | PromotionOfferDto | PromotionNewbieDto;
export type PromotionListItemAction<T extends PromotionCommonDto> = (item: T) => void;
export type PromotionListItemActions<T extends PromotionCommonDto> = {
	onEdit?: PromotionListItemAction<T>;
	onDelete?: PromotionListItemAction<T>;
};

const StyledCard = styled(Card)({
	position: 'relative',
	height: '100%',
	width: 170,
	minWidth: 170,
	'& img': {
		pointerEvents: 'none',
		userSelect: 'none',
	},
});

type PromotionDraggableListCardProps<T extends PromotionCommonDto> = CardProps & {
	item: T;
	actions: PromotionListItemActions<T>;
};

export const PromotionDraggableListCard = observer(
	<T extends PromotionCommonDto>({ children, item, actions, ...props }: PromotionDraggableListCardProps<T>) => {
		const [promotion] = useService(PromotionService);
		const fragrance = promotion.fragrances.find(f => f.id === item.fragranceId);
		if (!fragrance) return null;

		const offer = 'storeOfferId' in item ? fragrance.offers.find(offer => offer.id === item.storeOfferId) : null;
		const label = offer && offer.discountPrice ? ((offer.price - offer.discountPrice) * 100) / offer.price : null;
		const price = offer?.discountPrice ?? offer?.price;

		const inactive = !fragrance.active || ('storeOfferId' in item && offer?.available !== true);

		return (
			<StyledCard {...props}>
				<Col position="relative" height="100%" justifyContent="flex-start">
					{label && (
						<Row
							p={0.5}
							top={4}
							right={0}
							minWidth={50}
							position="absolute"
							bgcolor="warning.main"
							justifyContent="center"
							borderRadius="25px 0 0 25px"
						>
							<Text color="white" variant="body2">
								-{label.toFixed(0)}%
							</Text>
						</Row>
					)}
					<Img src={fragrance.photo} alt={fragrance.name} height={150} />
					<Typography
						variant="body2"
						textAlign="center"
						color={inactive ? 'gray' : undefined}
						sx={{ textDecoration: 'underline', zIndex: 1 }}
					>
						<Link to={AppRoutes.FragranceEdit.buildPath({ id: fragrance.id })} target="_blank">
							{fragrance.name}
						</Link>
					</Typography>
					{inactive ? (
						<>
							<Box
								position="absolute"
								top={0}
								left={0}
								right={0}
								height="100%"
								bgcolor={({ palette }) => palette.grey[200] + '80'}
							/>
							<Text textAlign="center" fontWeight="bold" color="error.light" zIndex={1}>
								INACTIVE
							</Text>
						</>
					) : (
						price !== undefined && (
							<Text textAlign="center" fontWeight="bold">
								{price} {offer?.currency}
							</Text>
						)
					)}
				</Col>

				<Row
					position="absolute"
					justifyContent="center"
					alignItems="center"
					gap={2}
					bottom={0}
					left={0}
					right={0}
					py={0.5}
					bgcolor="grey.200"
					sx={{ opacity: 0.8 }}
				>
					{actions.onEdit && (
						<IconButton size="small" onClick={() => actions.onEdit?.(item)}>
							<Edit />
						</IconButton>
					)}
					{actions.onDelete && (
						<IconButton size="small" onClick={() => actions.onDelete?.(item)}>
							<RemoveCircle />
						</IconButton>
					)}
				</Row>
			</StyledCard>
		);
	},
);
